import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Bell, Volume2, Globe, Moon, Vibrate, BellRing, Shield, Lock, CreditCard, User, HelpCircle, FileText, LogOut } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";

const ClientSettingsPage = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [settings, setSettings] = useState({
    push_notifications: true,
    booking_updates: true,
    promotions: false,
    sound_alerts: true,
    vibration: true,
    dark_mode: false,
  });
  const [language, setLanguage] = useState("en");

  const toggle = (key) => {
    setSettings((prev) => ({ ...prev, [key]: !prev[key] }));
    toast.success("Setting updated");
  };

  const notifSettings = [
    { key: "push_notifications", icon: Bell, label: "Push Notifications", desc: "Get notified about booking updates and status changes" },
    { key: "booking_updates", icon: BellRing, label: "Booking Alerts", desc: "Receive alerts when your booking status changes" },
    { key: "promotions", icon: FileText, label: "Promotions & Offers", desc: "Get notified about deals and special offers" },
    { key: "sound_alerts", icon: Volume2, label: "Sound Effects", desc: "Play sounds for notifications" },
    { key: "vibration", icon: Vibrate, label: "Vibration", desc: "Vibrate on notifications" },
  ];

  const quickLinks = [
    { to: "/profile", icon: User, label: "My Profile", desc: "Edit your name, email, and phone" },
    { to: "/my-bookings", icon: FileText, label: "My Bookings", desc: "View all your past and active bookings" },
    { to: "/payment-methods", icon: CreditCard, label: "Payment Methods", desc: "Manage your saved cards" },
    { to: "/privacy", icon: Lock, label: "Privacy Policy", desc: "Privacy settings and data" },
    { to: "/support", icon: HelpCircle, label: "Help & Support", desc: "Get help with your account or bookings" },
  ];

  const languages = [
    { value: "en", label: "English" },
    { value: "fr", label: "French" },
    { value: "es", label: "Spanish" },
    { value: "zh", label: "Chinese" },
    { value: "ar", label: "Arabic" },
  ];

  return (
    <div className="min-h-screen bg-stone-50" data-testid="client-settings-page">
      <Navbar />
      <div className="pt-24 pb-16 px-4 max-w-3xl mx-auto">
        <Link to="/" className="inline-flex items-center gap-2 text-stone-500 hover:text-emerald-950 mb-6 transition-colors text-sm" data-testid="back-link">
          <ArrowLeft className="w-4 h-4" /> Home
        </Link>
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
          <h1 className="text-2xl font-bold text-emerald-950 mb-6" data-testid="settings-title">
          </h1>

          {/* Quick Links */}
          <div className="mb-6">
            <p className="text-sm font-semibold text-stone-400 uppercase tracking-wider mb-3 px-1">Account</p>
            <div className="space-y-2">
              {quickLinks.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  className="bg-white rounded-2xl border border-stone-100 p-4 flex items-center gap-3 hover:border-emerald-200 transition-colors block"
                  data-testid={`link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <div className="w-9 h-9 bg-emerald-50 rounded-lg flex items-center justify-center shrink-0">
                    <item.icon className="w-4 h-4 text-emerald-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-emerald-950 text-sm">{item.label}</p>
                    <p className="text-xs text-stone-500">{item.desc}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>

          {/* Notifications */}
          <div className="mb-6">
            <p className="text-sm font-semibold text-stone-400 uppercase tracking-wider mb-3 px-1">Notifications</p>
            <div className="space-y-2">
              {notifSettings.map((item) => (
                <div key={item.key} className="bg-white rounded-2xl border border-stone-100 p-4 flex items-center justify-between" data-testid={`setting-${item.key}`}>
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    <div className="w-9 h-9 bg-stone-100 rounded-lg flex items-center justify-center shrink-0 mt-0.5">
                      <item.icon className="w-4 h-4 text-stone-600" />
                    </div>
                    <div className="min-w-0">
                      <Label className="font-medium text-emerald-950">{item.label}</Label>
                      <p className="text-xs text-stone-500 mt-0.5">{item.desc}</p>
                    </div>
                  </div>
                  <Switch checked={settings[item.key]} onCheckedChange={() => toggle(item.key)} className="shrink-0 ml-3" />
                </div>
              ))}
            </div>
          </div>

          {/* Language */}
          <div className="mb-6">
            <p className="text-sm font-semibold text-stone-400 uppercase tracking-wider mb-3 px-1">Language</p>
            <div className="bg-white rounded-2xl border border-stone-100 p-4" data-testid="language-section">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-9 h-9 bg-stone-100 rounded-lg flex items-center justify-center">
                  <Globe className="w-4 h-4 text-stone-600" />
                </div>
                <Label className="font-medium text-emerald-950">Language</Label>
              </div>
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                {languages.map((lang) => (
                  <button
                    key={lang.value}
                    onClick={() => { setLanguage(lang.value); toast.success(`Language set to ${lang.label}`); }}
                    className={`py-2 px-3 rounded-xl text-sm font-medium transition-all ${language === lang.value ? "bg-emerald-600 text-white" : "bg-stone-50 text-stone-600 hover:bg-stone-100"}`}
                    data-testid={`lang-${lang.value}`}
                  >
                    {lang.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* App Preferences */}
          <div className="mb-6">
            <p className="text-sm font-semibold text-stone-400 uppercase tracking-wider mb-3 px-1">Preferences</p>
            <div className="bg-white rounded-2xl border border-stone-100 p-4 flex items-center justify-between" data-testid="setting-dark_mode">
              <div className="flex items-start gap-3 flex-1 min-w-0">
                <div className="w-9 h-9 bg-stone-100 rounded-lg flex items-center justify-center shrink-0 mt-0.5">
                  <Moon className="w-4 h-4 text-stone-600" />
                </div>
                <div className="min-w-0">
                  <Label className="font-medium text-emerald-950">Dark Mode</Label>
                  <p className="text-xs text-stone-500 mt-0.5">Use dark theme throughout the app</p>
                </div>
              </div>
              <Switch checked={settings.dark_mode} onCheckedChange={() => toggle("dark_mode")} className="shrink-0 ml-3" />
            </div>
          </div>

          {/* Sign Out */}
          <button
            onClick={() => { logout(); navigate("/"); }}
            className="w-full bg-white rounded-2xl border border-red-100 p-4 flex items-center gap-3 text-red-600 hover:bg-red-50 transition-colors"
            data-testid="sign-out-btn"
          >
            <div className="w-9 h-9 bg-red-50 rounded-lg flex items-center justify-center shrink-0">
              <LogOut className="w-4 h-4 text-red-500" />
            </div>
            <p className="font-medium text-sm">Sign Out</p>
          </button>
        </motion.div>
      </div>
      <Footer />
    </div>
  );
};

export default ClientSettingsPage;
